<template>
  <div class="min-h-screen bg-[#FAF8F8] px-4 sm:px-6 lg:px-8 py-8">
    <div class="max-w-7xl mx-auto space-y-6">

      <!-- En-tête avec Titre & Bouton Nouveau Projet -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl sm:text-3xl font-extrabold text-[#0B1B3D] tracking-tight">
              Espaces de Projets
            </h1>
            <span class="bg-purple-100 text-[#7C3AED] text-xs font-bold px-2.5 py-0.5 rounded-full">
              {{ projects.length }}
            </span>
          </div>
          <p class="text-gray-500 text-sm mt-1">
            Pilotez, planifiez et suivez l'avancement de vos livrables collaboratifs.
          </p>
        </div>

        <!-- Action création -->
        <button @click="showForm = !showForm"
          style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
          class="flex items-center justify-center gap-2 text-white font-bold px-5 py-2.5 rounded-xl shadow-md hover:opacity-95 active:scale-[0.99] transition text-sm whitespace-nowrap">
          <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-45': showForm }" fill="none"
            stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
          </svg>
          <span>{{ showForm ? 'Fermer le formulaire' : 'Nouveau projet' }}</span>
        </button>
      </div>

      <!-- Barre de Statistiques / KPIs Projets -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
          <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Projets</span>
          <p class="text-2xl font-extrabold text-[#0B1B3D] mt-1">{{ projects.length }}</p>
        </div>
        <div class="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
          <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">En cours</span>
          <p class="text-2xl font-extrabold text-[#F97316] mt-1">
            {{ countStatus('En cours') }}
          </p>
        </div>
        <div class="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
          <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">En révision</span>
          <p class="text-2xl font-extrabold text-[#7C3AED] mt-1">
            {{ countStatus('En révision') }}
          </p>
        </div>
        <div class="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs">
          <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Livrés / Terminés</span>
          <p class="text-2xl font-extrabold text-emerald-600 mt-1">
            {{ countStatus('Terminé') }}
          </p>
        </div>
      </div>

      <!-- Formulaire de création (pliable) -->
      <section v-if="showForm" class="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
        <h2 class="text-lg font-bold text-[#0B1B3D] mb-4">Créer un nouvel espace projet</h2>
        <form @submit.prevent="addProject" class="flex flex-col gap-4">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input v-model="newProject.title" placeholder="Nom du projet (ex: Application Mobile)" required
              class="p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition text-sm" />
            <input v-model="newProject.description" placeholder="Description sommaire" required
              class="p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition text-sm" />
            <input v-model="newProject.project_type" placeholder="Catégorie (ex: Web, SNS, Industrie)" required
              class="p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition text-sm" />
          </div>
          <div class="flex justify-end gap-3">
            <button type="button" @click="showForm = false"
              class="px-4 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition">
              Annuler
            </button>
            <button type="submit" :disabled="isCreating"
              style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
              class="text-white font-bold px-6 py-2.5 rounded-xl shadow hover:opacity-95 transition disabled:opacity-50 text-sm">
              {{ isCreating ? 'Création...' : 'Valider et Créer' }}
            </button>
          </div>
        </form>
        <p v-if="createError" class="text-red-500 text-xs font-semibold mt-2">
          {{ createError }}
        </p>
      </section>

      <!-- Barre de Contrôle : Filtres par Statuts & Recherche -->
      <div
        class="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <!-- Filtres par pilules -->
        <div class="flex flex-wrap items-center gap-1.5">
          <button v-for="filter in ['Tous', ...STATUSES]" :key="filter" @click="selectedFilter = filter"
            class="px-3 py-1.5 rounded-xl text-xs font-semibold transition" :class="selectedFilter === filter
              ? 'bg-[#0B1B3D] text-white shadow-xs'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'">
            {{ filter }}
          </button>
        </div>

        <!-- Recherche textuelle -->
        <div class="relative w-full md:w-72">
          <input v-model="search" type="text" placeholder="Rechercher par mot-clé..."
            class="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition text-xs text-gray-800" />
          <svg class="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <!-- Liste / Grille des Projets -->
      <div>
        <!-- État : Chargement -->
        <div v-if="isLoading" class="text-gray-400 text-sm py-16 text-center flex flex-col items-center gap-2">
          <svg class="animate-spin h-7 w-7 text-[#7C3AED]" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          Chargement de vos espaces de travail...
        </div>

        <!-- État : Aucun résultat (sécurisé sans guillemets conflictuels) -->
        <div v-else-if="filteredProjects.length === 0"
          class="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-12 text-center">
          <div class="w-12 h-12 rounded-full bg-purple-50 text-[#7C3AED] flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 class="text-base font-bold text-[#0B1B3D]">Aucun projet trouvé</h3>
          <p v-if="search" class="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            Aucun projet ne correspond à votre recherche.
          </p>
          <p v-else class="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            Commencez par créer votre premier projet en haut à droite !
          </p>
        </div>

        <!-- Grille de cartes enrichies -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="project in filteredProjects" :key="project.id"
            class="bg-white border border-gray-100 hover:border-[#7C3AED]/30 rounded-2xl p-5 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between group">
            <!-- Haut de carte : Titre + Type -->
            <div class="cursor-pointer" @click="$router.push(`/projects/${project.id}`)">
              <div class="flex items-start justify-between gap-3">
                <h3 class="font-bold text-[#0B1B3D] text-base group-hover:text-[#7C3AED] transition">
                  {{ project.title }}
                </h3>
                <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0"
                  :class="typeBadgeClass(project.project_type)">
                  {{ project.project_type || 'Standard' }}
                </span>
              </div>
              <p class="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
                {{ project.description }}
              </p>
            </div>

            <!-- Milieu : Progression visuelle -->
            <div class="mt-4 pt-4 border-t border-gray-100">
              <div class="flex items-center justify-between text-xs mb-1.5">
                <span class="text-gray-400 font-medium">Progression</span>
                <span class="font-bold text-[#0B1B3D]">
                  {{ project.lots_count ? `${project.lots_count} lot(s)` : 'Autonome' }}
                </span>
              </div>
              <div class="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div class="h-full transition-all duration-300 rounded-full" :style="{
                  width: project.status === 'Terminé' ? '100%' : project.status === 'En cours' ? '60%' : '25%',
                  background: project.status === 'Terminé' ? '#10B981' : 'linear-gradient(90deg, #7C3AED, #F97316)'
                }"></div>
              </div>
            </div>

            <!-- Pied de Carte : Statut & Lien Lots -->
            <div class="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2" @click.stop>
              <!-- Sélecteur de statut -->
              <select :value="project.status" :disabled="updatingStatusId === project.id"
                @change="updateStatus(project, $event.target.value)"
                class="text-xs font-semibold px-2.5 py-1 rounded-lg border-0 outline-none cursor-pointer disabled:opacity-50 transition"
                :class="statusBadgeClass(project.status)">
                <option v-for="s in STATUSES" :key="s" :value="s">
                  {{ s }}
                </option>
              </select>

              <!-- Dérouler les lots -->
              <button @click="toggleLots(project)"
                class="text-xs font-semibold text-[#F97316] hover:text-[#ea580c] flex items-center gap-1 transition">
                {{ expandedProjectId === project.id ? 'Masquer' : 'Voir les lots' }}
                <svg class="w-3.5 h-3.5 transition-transform duration-150"
                  :class="{ 'rotate-180': expandedProjectId === project.id }" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <!-- Tiroir des lots -->
            <div v-if="expandedProjectId === project.id" class="mt-3 pt-3 border-t border-gray-100 space-y-2.5"
              @click.stop>
              <div v-if="loadingLots" class="text-xs text-gray-400 text-center py-2">
                Chargement...
              </div>

              <template v-else>
                <div v-for="lot in lotsByProject[project.id] || []" :key="lot.id"
                  class="bg-[#FAF8F8] border border-gray-100 rounded-xl p-2.5">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-xs font-bold text-[#0B1B3D] truncate">{{ lot.name }}</p>
                      <p v-if="lot.start_date || lot.end_date" class="text-[10px] text-gray-400 mt-0.5">
                        {{ formatDate(lot.start_date) }} → {{ formatDate(lot.end_date) }}
                      </p>
                    </div>
                    <select :value="lot.status" @change="updateLotStatus(project, lot, $event.target.value)"
                      class="text-[11px] font-semibold px-2 py-0.5 rounded-md border-0 outline-none cursor-pointer"
                      :class="statusBadgeClass(lot.status)">
                      <option v-for="s in STATUSES" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </div>
                </div>

                <p v-if="(lotsByProject[project.id] || []).length === 0"
                  class="text-[11px] text-gray-400 text-center py-1">
                  Aucun lot pour le moment.
                </p>

                <!-- Formulaire Rapide Ajout Lot -->
                <form @submit.prevent="addLot(project)" class="flex flex-col gap-2 pt-2 border-t border-gray-100">
                  <input v-model="newLot.name" placeholder="Nom du lot..." required
                    class="text-xs p-2 bg-white border border-gray-200 rounded-lg focus:border-[#7C3AED] outline-none" />
                  <button type="submit" :disabled="isCreatingLot"
                    class="text-xs bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold py-1.5 px-3 rounded-lg transition disabled:opacity-50">
                    {{ isCreatingLot ? 'Ajout...' : '+ Ajouter ce lot' }}
                  </button>
                </form>
              </template>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

const TYPE_COLORS = [
  'bg-purple-50 text-[#7C3AED] border border-purple-200',
  'bg-fuchsia-50 text-[#C026D3] border border-fuchsia-200',
  'bg-orange-50 text-[#F97316] border border-orange-200',
  'bg-amber-50 text-amber-700 border border-amber-200',
  'bg-blue-50 text-blue-700 border border-blue-200',
  'bg-emerald-50 text-emerald-700 border border-emerald-200'
]

const STATUSES = [
  'Brouillon',
  'En cours',
  'En révision',
  'Terminé',
  'En maintenance'
]

const STATUS_COLORS = {
  Brouillon: 'bg-gray-100 text-gray-600',
  'En cours': 'bg-orange-100 text-[#F97316]',
  'En révision': 'bg-purple-100 text-[#7C3AED]',
  Terminé: 'bg-emerald-100 text-emerald-700 font-bold',
  'En maintenance': 'bg-fuchsia-100 text-[#C026D3]'
}

export default {
  name: 'ProjectsView',
  data() {
    return {
      projects: [],
      search: '',
      selectedFilter: 'Tous',
      isLoading: false,
      error: null,
      showForm: false,
      newProject: { title: '', description: '', project_type: '' },
      isCreating: false,
      createError: null,
      updatingStatusId: null,
      expandedProjectId: null,
      lotsByProject: {},
      loadingLots: false,
      newLot: { name: '', description: '', start_date: '', end_date: '' },
      isCreatingLot: false,
      STATUSES
    }
  },
  computed: {
    filteredProjects() {
      return this.projects.filter(p => {
        const matchesFilter = this.selectedFilter === 'Tous' || p.status === this.selectedFilter
        const term = this.search.trim().toLowerCase()
        const matchesSearch = !term ||
          p.title?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.project_type?.toLowerCase().includes(term)
        return matchesFilter && matchesSearch
      })
    }
  },
  methods: {
    countStatus(statusName) {
      return this.projects.filter(p => p.status === statusName).length
    },
    async fetchProjects() {
      this.isLoading = true
      this.error = null
      try {
        const res = await apiClient.get('/api/v1/projects')
        this.projects = res.data
      } catch (err) {
        this.error = 'Erreur lors du chargement des projets.'
        console.error(err)
      } finally {
        this.isLoading = false
      }
    },
    async addProject() {
      this.isCreating = true
      this.createError = null
      try {
        const res = await apiClient.post('/api/v1/projects', { project: this.newProject })
        this.newProject = { title: '', description: '', project_type: '' }
        this.showForm = false
        const projectId = res.data?.id || res.data?.project?.id
        if (projectId) {
          this.$router.push(`/projects/${projectId}`)
        } else {
          await this.fetchProjects()
        }
      } catch (err) {
        this.createError = 'Erreur lors de la création du projet.'
      } finally {
        this.isCreating = false
      }
    },
    async updateStatus(project, newStatus) {
      if (newStatus === project.status) return
      const previous = project.status
      this.updatingStatusId = project.id
      project.status = newStatus
      try {
        await apiClient.patch(`/api/v1/projects/${project.id}`, { project: { status: newStatus } })
      } catch (err) {
        project.status = previous
      } finally {
        this.updatingStatusId = null
      }
    },
    async toggleLots(project) {
      if (this.expandedProjectId === project.id) {
        this.expandedProjectId = null
        return
      }
      this.expandedProjectId = project.id
      this.newLot = { name: '', description: '', start_date: '', end_date: '' }
      if (!this.lotsByProject[project.id]) {
        await this.fetchLots(project)
      }
    },
    async fetchLots(project) {
      this.loadingLots = true
      try {
        const res = await apiClient.get(`/api/v1/projects/${project.id}/lots`)
        this.lotsByProject = { ...this.lotsByProject, [project.id]: res.data }
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingLots = false
      }
    },
    async addLot(project) {
      this.isCreatingLot = true
      try {
        await apiClient.post(`/api/v1/projects/${project.id}/lots`, { lot: this.newLot })
        this.newLot = { name: '', description: '', start_date: '', end_date: '' }
        await this.fetchLots(project)
        await this.fetchProjects()
      } catch (err) {
        console.error(err)
      } finally {
        this.isCreatingLot = false
      }
    },
    async updateLotStatus(project, lot, newStatus) {
      lot.status = newStatus
      try {
        await apiClient.patch(`/api/v1/projects/${project.id}/lots/${lot.id}`, { lot: { status: newStatus } })
        await this.fetchProjects()
      } catch (err) {
        console.error(err)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '?'
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      })
    },
    statusBadgeClass(status) {
      return STATUS_COLORS[status] || STATUS_COLORS.Brouillon
    },
    typeBadgeClass(type) {
      const label = type || 'Standard'
      let hash = 0
      for (let i = 0; i < label.length; i++) {
        hash = (hash * 31 + label.charCodeAt(i)) % TYPE_COLORS.length
      }
      return TYPE_COLORS[Math.abs(hash)]
    }
  },
  mounted() {
    this.fetchProjects()
  }
}
</script>
